<template>
  <AdminLayout>
    <Head title="Order Details" />

    <template #pageAlerts>
      <SuccessFlash :flash="flash"></SuccessFlash>
    </template>

    <!-- Order header -->
    <div class="headline flex flex-col-reverse md:flex-row justify-between items-start md:items-center mx-4 sm:mx-12">
      <h2
        class="text-xl sm:text-2xl font-semibold pb-5 pt-5 text-blue-600 flex flex-col md:flex-row items-start md:items-end"
      >
        <span class="flex flex-col">
          <span class="page-type text-sm sm:text-base">Order</span>
          <span :class="{ 'text-red-400': order.flags?.length > 0 }">Order #{{ order.reference_number }}</span>
          <FlagStatus
            :flags="order.flags"
            :show-count="showFlaggedCount"
            class="mt-2"
          />
        </span>
      </h2>

      <div class="relative inline-flex shadow-sm rounded-md md:items-start items-end self-end md:self-auto mt-2">
        <Menu
          as="div"
          class="relative inline-block text-left"
        >
          <div>
            <MenuButton
              class="inline-flex justify-center w-full rounded-md border border-gray-400 shadow-sm px-4 py-3 bg-gray-100 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-400 focus:ring-indigo-500"
            >
              Actions
              <ChevronDownIcon
                class="-mr-1 ml-2 h-5 w-5"
                aria-hidden="true"
              />
            </MenuButton>
          </div>

          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
          >
            <MenuItems
              class="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-gray-100 ring-1 ring-gray-400 ring-opacity-5 divide-y divide-gray-400 focus:outline-none z-50"
            >
              <div
                class="py-1"
                v-if="hasPermission('trigger_order_events')"
              >
                <MenuItem v-slot="{ active }">
                  <a
                    @click.prevent="openFireEventModal()"
                    href="#"
                    :class="[
                      'text-gray-700 hover:text-gray-200 hover:bg-blue-600 group flex items-center px-4 py-2 text-sm'
                    ]"
                  >
                    Trigger Event
                  </a>
                </MenuItem>
              </div>
              <div class="py-1">
                <MenuItem v-slot="{ active }">
                  <a
                    @click.prevent="showOrderSticker()"
                    href="#"
                    :class="[
                      'text-gray-700 hover:text-gray-200 hover:bg-blue-600 group flex items-center px-4 py-2 text-sm'
                    ]"
                  >
                    Show Order Stickers
                  </a>
                </MenuItem>
                <MenuItem v-slot="{ active }">
                  <a
                    @click.prevent="showOrderReceipt()"
                    href="#"
                    :class="[
                      'text-gray-700 hover:text-gray-200 hover:bg-blue-600 group flex items-center px-4 py-2 text-sm'
                    ]"
                  >
                    Show Order Receipt
                  </a>
                </MenuItem>
              </div>
              <div class="py-1">
                <MenuItem v-slot="{ active }">
                  <a
                    @click.prevent="triggerPrintOrder()"
                    href="#"
                    :class="[
                      'text-gray-700 hover:text-gray-200 hover:bg-blue-600 group flex items-center px-4 py-2 text-sm'
                    ]"
                  >
                    Print Order
                  </a>
                </MenuItem>
                <MenuItem v-slot="{ active }">
                  <a
                    @click.prevent="triggerPrintOrderReceipt()"
                    href="#"
                    :class="[
                      'text-gray-700 hover:text-gray-200 hover:bg-blue-600 group flex items-center px-4 py-2 text-sm'
                    ]"
                  >
                    Print Order Receipt
                  </a>
                </MenuItem>
                <MenuItem v-slot="{ active }">
                  <a
                    @click.prevent="triggerPrintOrderSticker()"
                    href="#"
                    :class="[
                      'text-gray-700 hover:text-gray-200 hover:bg-blue-600 group flex items-center px-4 py-2 text-sm'
                    ]"
                  >
                    Print Order Sticker
                  </a>
                </MenuItem>
              </div>
              <div
                v-if="['cancelled', 'failed', 'voided'].includes(order.status)"
                class="py-1"
              >
                <MenuItem v-slot="{ active }">
                  <a
                    @click.prevent="returnOrderStock()"
                    href="#"
                    :class="[
                      'text-gray-700 hover:text-gray-200 hover:bg-blue-600 group flex items-center px-4 py-2 text-sm'
                    ]"
                  >
                    Return Order Inventory
                  </a>
                </MenuItem>
              </div>
              <div
                v-if="!['pending'].includes(order.status)"
                class="py-1"
              >
                <MenuItem v-slot="{ active }">
                  <a
                    @click.prevent="reDispenseOrder()"
                    href="#"
                    :class="[
                      'text-gray-700 hover:text-gray-200 hover:bg-blue-600 group flex items-center px-4 py-2 text-sm'
                    ]"
                  >
                    <template v-if="['cancelled', 'failed', 'voided'].includes(order.status)">
                      Return Dispensation
                    </template>
                    <template v-else> Re-Dispense Order </template>
                  </a>
                </MenuItem>
              </div>
              <div
                v-if="!['cancelled', 'voided'].includes(order.status)"
                class="py-1"
              >
                <MenuItem v-slot="{ active }">
                  <a
                    @click.prevent="cancelOrder()"
                    href="#"
                    :class="[
                      'text-gray-700 hover:text-gray-200 hover:bg-blue-600 group flex items-center px-4 py-2 text-sm'
                    ]"
                  >
                    Cancel Order
                  </a>
                </MenuItem>
              </div>
              <div
                class="py-1"
                v-if="isAdmin && novaRoute"
              >
                <MenuItem v-slot="{ active }">
                  <NovaMenuButton :nova-route="novaRoute" />
                </MenuItem>
              </div>
            </MenuItems>
          </transition>
        </Menu>
      </div>
    </div>
    <!-- END Order header -->

    <!-- Order tabs -->
    <div
      class="tabs flex-1 flex flex-col"
      :class="{
        'overflow-hidden': pageLayout.shouldLock.value
      }"
    >
      <dl
        class="mb-5 flex sm:rounded-lg bg-gray-100 overflow-y-hidden overflow-x-scroll shadow divide-gray-400 divide-x sm:mx-12"
      >
        <div
          v-for="item in stats"
          :key="item.name"
          class="shrink-0 w-9/12 sm:flex-1 px-4 py-5 sm:p-6"
        >
          <dt class="text-base font-semibold text-blue-600">
            {{ item.name }}
          </dt>
          <dd class="mt-1 flex justify-between items-baseline md:block lg:flex">
            <div class="flex items-baseline text-2xl font-semibold text-blue-600">
              {{ item.stat }}
              <span
                v-if="item.previousStat"
                class="ml-2 text-sm font-medium text-gray-600"
              >
                from {{ item.previousStat }}
              </span>
            </div>

            <div
              v-if="item.change && item.changeType"
              :class="[
                item.changeType === 'increase' ? 'bg-green-200 text-green-800' : 'bg-red-100 text-red-800',
                'inline-flex items-baseline px-2.5 py-0.5 rounded-full text-sm font-medium md:mt-2 lg:mt-0'
              ]"
            >
              <ArrowSmUpIcon
                v-if="item.changeType === 'increase'"
                class="-ml-1 mr-0.5 shrink-0 self-center h-5 w-5 text-green-400"
                aria-hidden="true"
              />
              <ArrowSmDownIcon
                v-else
                class="-ml-1 mr-0.5 shrink-0 self-center h-5 w-5 text-red-400"
                aria-hidden="true"
              />
              <span class="sr-only"> {{ item.changeType === 'increase' ? 'Increased' : 'Decreased' }} by </span>
              {{ item.change }}
            </div>
          </dd>
        </div>
      </dl>

      <PageTabGroup>
        <PageTabList>
          <template v-for="tab in pageTabs">
            <PageTab
              v-if="tab.visible"
              :key="tab"
              :label="tab.label"
              :is-flagged="tab.flagged"
              :show-indicator-count="showFlaggedCount"
              :indicator-count="tab.flaggedCount"
            />
          </template>
        </PageTabList>

        <PageTabPanels>
          <PageTabPanel>
            <div class="md:grid md:grid-cols-2 md:gap-10">
              <div>
                <div class="md:border-b md:border-gray-400 md:pb-5 md:mb-5">
                  <h3 class="text-lg leading-6 font-semibold text-blue-600">Order Details</h3>
                </div>

                <dl class="md:grid md:grid-cols-1 divide-y divide-gray-200">
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600">Number</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      {{ order.id }}
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600">Status</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      {{ order.display_status }}
                      <span
                        v-if="order.last_returned_at"
                        class="ml-2 text-gray-600 text-sm"
                      >
                        (Returned)
                      </span>
                      <br />
                      <span
                        v-if="order.cancelled_by_id"
                        class="mt-1 text-gray-600 text-xs"
                      >
                        {{ order.cancelled_by.name }}
                      </span>
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600">Inventory Location</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      {{ order.location_name }}
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600">Fulfillment Location</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      {{ order.fulfillment_location_name }}
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600">Register</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      <span v-if="order.register">
                        ( {{ order.register.location_id }} ) {{ order.register.name }}
                      </span>
                      <span v-else> - </span>
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600">Created</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      {{ _datetime(order.created_at) }}
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600">Origin</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      {{ order.origin }}
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600">Origin ID</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      {{ order.source_id || '-' }}
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600">Delivery Method</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      {{ order.display_delivery_method }}
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600">Subtotal</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      {{ order.subtotal ? _dollars(order.subtotal) : '-' }}
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600">Discount</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      {{ order.total_discount ? _dollars(order.total_discount) : '-' }}
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600">Discount Name</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      {{ discountNames || '-' }}
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600">Total Fees</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      {{ order.total_fees ? _dollars(order.total_fees) : '-' }}
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600">Total Tax</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      {{ order.total_tax ? _dollars(order.total_tax) : '-' }}
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600">Total Refund</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      {{ order.total_refund ? _dollars(order.total_refund) : '-' }}
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600">Refund Reason</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      {{ order.refund_reason ?? '-' }}
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600">Total</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      {{ order.total ? _dollars(order.total) : '-' }}
                    </dd>
                  </div>
                </dl>
              </div>
              <div>
                <div class="md:border-b md:border-gray-400 md:pb-5 md:mb-5">
                  <h3 class="text-lg leading-6 font-semibold text-blue-600">Customer</h3>
                </div>
                <dl class="divide-y divide-gray-200 mb-6">
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600">Email</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      <Link
                        v-if="order.customer?.id"
                        :href="showCustomerRoute(order.customer?.id)"
                        class="uppercase font-semibold text-blue-600 hover:underline hover:text-blue-600"
                      >
                        {{ order.customer?.email || '-' }}
                      </Link>
                      <span v-else>
                        {{ order.customer?.email || '-' }}
                      </span>
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600">Name</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      {{ order.customer?.first_name || '' }} {{ order.customer?.last_name || '' }}
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600">Phone</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      {{ order.customer?.phone || '-' }}
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600">Has Loyalty</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      {{ order.customer?.has_loyalty ? 'Yes' : 'No' }}
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600">Veteran</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      {{ order.customer?.is_veteran ? 'Yes' : 'No' }}
                    </dd>
                  </div>
                </dl>
                <div class="md:border-b md:border-gray-400 md:pb-5 md:mb-5">
                  <h3 class="text-lg leading-6 font-semibold text-blue-600">Dispensation</h3>
                </div>
                <dl class="divide-y divide-gray-200 mb-6">
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600">Status</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      {{ order.medical.status }}
                    </dd>
                  </div>
                  <div
                    v-for="route in order.medical.route_dispensations"
                    class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2"
                  >
                    <dt class="text-sm font-medium text-blue-600">
                      {{ route.medical_route_name }}
                    </dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">{{ route.amount }}{{ route.unit }}</dd>
                  </div>
                  <!--                                    <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">-->
                  <!--                                        <dt class="text-sm font-medium text-blue-600">-->
                  <!--                                            Response-->
                  <!--                                        </dt>-->
                  <!--                                        <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">-->
                  <!--                                            <a href="#">View Biotrack Response</a>-->
                  <!--                                        </dd>-->
                  <!--                                    </div>-->
                </dl>
              </div>
            </div>
          </PageTabPanel>

          <PageTabPanel>
            <div class="md:border-b md:border-gray-400 md:pb-5 md:mb-5">
              <h3 class="text-lg leading-6 font-semibold text-blue-600">Items</h3>
            </div>

            <data-table
              :items="items"
              :headers="['Product', 'Price', 'Quantity', 'Subtotal', 'Discount', 'Total', 'Refunded', '']"
              :actions="orderItemActions"
              :selectable="false"
            >
              <template v-slot:rows="slotRow">
                <order-item-index-row
                  v-slot:rows="slotRow"
                  v-bind="slotRow"
                  :selectable="false"
                  :show-route="showOrderItemRoute"
                  @manage="selectOrderItem(slotRow.item)"
                />
              </template>
            </data-table>
          </PageTabPanel>

          <PageTabPanel>
            <div class="md:grid md:grid-cols-2 md:gap-10 parent">
              <DeliveryInfo
                v-if="order.is_delivery"
                :shipping="order.shipping || {}"
                :shipped-at="order.shipped_at"
              ></DeliveryInfo>
              <div
                class="parent flex flex-col h-full"
                v-if="order.delivery"
              >
                <div class="flex-1">
                  <div class="md:border-b md:border-gray-400 md:pb-5 md:mb-5">
                    <h3 class="text-lg leading-6 font-semibold text-blue-600">Delivery Details</h3>
                  </div>
                  <ol class="divide-y divide-gray-400">
                    <li
                      class="py-4 flex gap-x-3"
                      v-if="order.delivery.delivered_at"
                    >
                      <ArrowRightCircleIcon class="w-6 h-6 text-gray-800 shrink-0 relative top-0.5" />
                      <div>
                        <p>
                          <span class="font-semibold">Delivery Attempt Success.</span>
                          From {{ order.location.name }} to {{ order.shipping.formatted_address }} by
                          {{ order.delivery.driver_one?.name ?? 'Unknown' }}
                          {{ order.delivery.driver_two?.name ? order.delivery.driver_two.name + '& ' : '' }}
                          {{ order.delivery.vehicle?.name ? ' in ' + order.delivery.vehicle.name : '' }}.
                          <span v-if="order.notes"
                            >Note from the driver: <i>{{ order.notes }}</i></span
                          >
                        </p>

                        <div
                          class="py-2 md:py-3 gap-4"
                          v-if="order.delivery.uploads?.length > 0"
                        >
                          <dt class="text-sm font-medium text-blue-600">Uploads:</dt>
                          <dd class="text-sm text-gray-900 md:mt-2 md:col-span-2">
                            <div class="flex space-x-2">
                              <div
                                v-for="upload in order.delivery.uploads"
                                class="h-32 w-32 rounded bg-gray-300 flex items-center justify-center"
                              >
                                <img
                                  :src="upload.data"
                                  :alt="upload.type"
                                />
                              </div>
                            </div>
                          </dd>
                        </div>
                        <p class="text-xs text-gray-600 pt-2">
                          <span class="font-semibold">Attempted:</span>
                          {{ moment(order.delivery.delivered_at).format('M/D/YY @ h:mm A') }}
                        </p>
                      </div>
                    </li>
                    <li
                      class="py-4 flex gap-x-3"
                      v-if="order.delivery.failed_at"
                    >
                      <ArrowRightCircleIcon class="w-6 h-6 text-gray-800 shrink-0 relative top-0.5" />
                      <div>
                        <p>
                          <span class="font-semibold">Delivery Attempt Failed.</span>
                          {{ order.delivery.notes }}
                        </p>
                        <p class="text-xs text-gray-600 pt-2">
                          <span class="font-semibold">Attempted:</span>
                          {{ moment(order.delivery.failed_at).format('M/D/YY @ h:mm A') }}
                        </p>
                      </div>
                    </li>
                  </ol>
                </div>
              </div>
              <div>
                <div class="md:border-b md:border-gray-400 md:pb-5 md:mb-5">
                  <h3 class="text-lg leading-6 font-semibold text-blue-600">Fulfillment Details</h3>
                </div>
                <dl class="md:divide-y md:divide-gray-200">
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600">Delivery Method</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      {{ order.display_delivery_method }}
                    </dd>
                  </div>
                </dl>
                <dl class="md:divide-y md:divide-gray-200">
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600">Location</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      {{ order.location?.name || 'NA' }}
                    </dd>
                  </div>
                </dl>
                <dl class="md:divide-y md:divide-gray-200">
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600">Fulfillment Date</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      {{ order.packed_at ? _date(order.packed_at) : 'NA' }}
                    </dd>
                  </div>
                </dl>
                <dl
                  class="md:divide-y md:divide-gray-200"
                  v-if="order.packed_by"
                >
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600">Fulfilled By</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      {{ order.packed_by.name }}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </PageTabPanel>

          <PageTabPanel>
            <div>
              <div class="md:border-b md:border-gray-400 md:pb-5 md:mb-5">
                <h3 class="text-lg leading-6 font-semibold text-blue-600">Payment</h3>
              </div>

              <data-table
                :items="order.payments"
                :headers="['Type', 'Method', 'Amount', 'Paid At']"
                :actions="[]"
                :selectable="false"
              >
                <template v-slot:rows="slotRow">
                  <payment-index-row
                    v-slot:rows="slotRow"
                    v-bind="slotRow"
                    :selectable="false"
                  />
                </template>
              </data-table>
            </div>
          </PageTabPanel>

          <PageTabPanel
            locked-height
            without-padding
          >
            <ActivityLog
              :resource-type="LOG_TYPES.ORDER"
              :resource-id="order.id"
            />
          </PageTabPanel>
        </PageTabPanels>
      </PageTabGroup>
    </div>
    <!-- END Order tabs -->

    <template #modals>
      <fire-event
        :show="currentModal === 'fireEvent'"
        :order="order"
        :event-options="eventOptions"
        @close="closeAllModals"
      />
    </template>

    <template #overlays>
      <OverlaySlideOver
        v-model:value="showingOrderItemDetails"
        @close="hideOrderItem()"
      >
        <template #header>
          <div v-if="selectedOrderItem">
            <h3 class="text-lg leading-6 font-medium text-gray-900">Line Item #{{ selectedOrderItem.id }}</h3>
            <p class="mt-1 max-w-2xl text-sm text-gray-600">Attached Packages</p>
          </div>
        </template>
        <order-item-manager
          v-if="selectedOrderItem"
          :item="selectedOrderItem"
          :location-id="order.location.id"
        />
      </OverlaySlideOver>
    </template>
  </AdminLayout>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useInitGridState } from '@/Components/DataGrid/Composables/grid-state'
import { useLayoutState } from '@/Layouts/layout-state'
import { ArrowDownCircleIcon, ArrowRightCircleIcon } from '@heroicons-2/vue/24/outline'
import { PageTabGroup, PageTabList, PageTab, PageTabPanels, PageTabPanel } from '@/Components/PageTabs'
import NovaMenuButton from '@/Components/Buttons/NovaMenuButton.vue'
import moment from 'moment'
import FlagStatus from '@/Components/Flags/FlagStatus.vue'
import { FLAG_TYPES, PERMISSIONS } from '@/constants'
import { useCompanyPermissions } from '@/Composables/permissions'
import { createPageTabs, addFlagsToPageTabs, shouldShowFlaggedCount } from '@/Components/PageTabs'
import FireEvent from '@/Pages/Orders/Components/FireEvent.vue'

const props = defineProps({
  order: {
    type: Object,
    required: true
  },
  items: {
    type: Array,
    required: true
  },
  flash: {
    type: Object,
    required: false
  },
  eventOptions: {
    type: Array,
    default: () => []
  }
})

const pageLayout = useLayoutState()
const { context } = useInitGridState()
const permissions = useCompanyPermissions()

const useTabSystem = () => {
  const TABS = {
    DETAILS: 'Details',
    ITEMS: 'Items',
    FULFILLMENT: 'Fulfillment',
    PAYMENT: 'Payment',
    ACTIVITY_LOG: 'Activity Log'
  }

  const FLAG_TAB_MAP = {
    [FLAG_TYPES.SYNC_ERROR]: TABS.ACTIVITY_LOG
  }

  const TAB_VISIBILITY = {
    [TABS.ACTIVITY_LOG]: permissions.userCan(PERMISSIONS.VIEW_ACTIVITY)
  }

  const pageTabs = createPageTabs({
    TABS,
    isVisible: (tab) => TAB_VISIBILITY[tab] ?? true
  })

  addFlagsToPageTabs({
    pageTabs,
    FLAG_TAB_MAP,
    flags: props.order.flags
  })

  const { showFlaggedCount } = shouldShowFlaggedCount(props.order.flags)

  return {
    pageTabs,
    showFlaggedCount
  }
}

const { pageTabs, showFlaggedCount } = useTabSystem()
</script>

<script>
import AdminLayout from '@/Layouts/AdminLayout.vue'
import { Head, Link } from '@inertiajs/vue3'
import { Switch } from '@headlessui/vue'
import { Menu, MenuButton, MenuItem, MenuItems, Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import {
  ArchiveIcon,
  ArrowCircleRightIcon,
  ChevronDownIcon,
  DuplicateIcon,
  HeartIcon,
  PencilAltIcon,
  TrashIcon,
  UserAddIcon
} from '@heroicons/vue/solid'
import { QrcodeIcon } from '@heroicons/vue/outline'
import { ChevronUpIcon, ArrowSmDownIcon, ArrowSmUpIcon } from '@heroicons/vue/solid'
import DataTable from '@/Components/Table/DataTable'
import OrderItemIndexRow from '@/Pages/Orders/Components/OrderItemIndexRow'
import PaymentIndexRow from '@/Pages/Orders/Components/PaymentIndexRow'
import OrderItemManager from '@/Pages/Orders/Components/OrderItemManager'
import _date from '@/Filters/Date'
import _datetime from '@/Filters/DateTime'
import _dollars from '@/Filters/Dollars'
import hasAuth from '@/Mixins/hasAuth'
import DeliveryInfo from '@/Pages/Orders/Components/DeliveryInfo'
import SuccessFlash from '@/Partials/Alerts/SuccessFlash'
import OverlaySlideOver from '@/Components/SlideOvers/OverlaySlideOver'
import ActivityLog from '@/Components/ActivityLog/ActivityLog.vue'
import { LOG_TYPES } from '@/Components/ActivityLog/log-types'
import { useConfirmDialog } from '@/Composables/confirm-dialog'
import { settle } from '@/utils/promises'

export default {
  mixins: [hasAuth],

  components: {
    ActivityLog,
    DeliveryInfo,
    AdminLayout,
    Head,
    Link,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    ArchiveIcon,
    ArrowCircleRightIcon,
    ChevronDownIcon,
    DuplicateIcon,
    HeartIcon,
    PencilAltIcon,
    TrashIcon,
    UserAddIcon,
    QrcodeIcon,
    Switch,
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    ChevronUpIcon,
    ArrowSmDownIcon,
    ArrowSmUpIcon,
    DataTable,
    OrderItemIndexRow,
    PaymentIndexRow,
    SuccessFlash,
    OverlaySlideOver,
    OrderItemManager
  },

  props: {
    /**
     * The order object being displayed.
     */
    order: {
      type: Object,
      required: true
    },
    /**
     * The order items for this order.
     */
    items: {
      type: Array,
      required: true
    },
    /**
     * Flash messages from the server
     */
    flash: {
      default: null
    }
  },

  data() {
    return {
      LOG_TYPES,
      stats: [],
      printForm: this.$inertia.form({
        orders: [this.order.id],
        print_receipt: true,
        print_sticker: true
      }),
      returnForm: this.$inertia.form({}),
      reDispenseForm: this.$inertia.form({}),
      cancelOrderForm: this.$inertia.form({}),
      selectedOrderItemId: null,
      showingOrderItemDetails: false,
      currentModal: null
    }
  },

  created() {
    this.stats.push({ name: 'Total', stat: _dollars(this.order.total || 0) })
    this.stats.push({ name: 'Items', stat: this.items.length })
    this.stats.push({ name: 'Paid', stat: _dollars(this.order.total_payment || 0) })
    this.stats.push({ name: 'Dispensation', stat: this.order.medical.status })
    if (this.order.delivery?.delivered_at) {
      this.stats.push({ name: 'Delivered On', stat: _date(this.order.delivery?.delivered_at) })
    }
  },

  computed: {
    showOrderItemRoute() {
      // A better argument name here would be 'package' but thats a reserved word in javascript
      return (item) => {
        if (!item.product_id) {
          return null
        }

        return route('products.edit', { product: item.product_id })
      }
    },
    selectedOrderItem() {
      if (!this.selectedOrderItemId) {
        return null
      }

      return _.find(this.items, (item) => {
        return item.id === this.selectedOrderItemId
      })
    },
    orderItemActions() {
      if (this.role === 'admin' || this.isCompanyAdminOrManager || this.isCompanyInventory) {
        return ['manage']
      }

      return []
    },
    discountNames() {
      if (this.order.discount_names) {
        return this.order.discount_names.join(', ')
      }

      return this.order.discount_name
    },
    novaRoute() {
      return route('nova.pages.detail', {
        resource: 'orders',
        resourceId: this.order.id
      })
    }
  },

  methods: {
    _date,
    _datetime,
    _dollars,
    showCustomerRoute(id) {
      return route('customers.show', { customer: id })
    },
    printOrder() {
      window.open(route('orders.sticker', { order: this.order.id }, '_blank'))
    },
    showOrderSticker() {
      window.open(route('orders.sticker', { order: this.order.id }, '_blank'))
    },
    showOrderReceipt() {
      window.open(route('orders.receipt', { order: this.order.id }, '_blank'))
    },
    triggerPrintOrder() {
      this.printForm.post(this.route('orders.print'))
    },
    triggerPrintOrderReceipt() {
      this.printForm
        .transform((data) => ({
          ...data,
          print_sticker: false
        }))
        .post(this.route('orders.print'))
    },
    triggerPrintOrderSticker() {
      this.printForm
        .transform((data) => ({
          ...data,
          print_receipt: false
        }))
        .post(this.route('orders.print'))
    },
    returnOrderStock() {
      this.returnForm.post(this.route('orders.return', { order: this.order.id }))
    },
    async reDispenseOrder() {
      const { show } = useConfirmDialog()
      const [error, data] = await settle(
        show({
          type: 'danger',
          confirmText: 'Re-Dispense',
          body: "Are you sure you want to re-dispense this order? This can't be undone."
        })
      )

      if (!error) {
        this.reDispenseForm.post(this.route('orders.re-dispense', { order: this.order.id }))
      }
    },
    async cancelOrder() {
      const { show } = useConfirmDialog()
      const [error, data] = await settle(
        show({
          type: 'danger',
          confirmText: 'Continue',
          body: "Are you sure you want to cancel this order? This can't be undone."
        })
      )

      if (!error) {
        this.cancelOrderForm.post(this.route('orders.cancel', { order: this.order.id }))
      }
    },
    selectOrderItem(orderItem) {
      this.selectedOrderItemId = orderItem.id
      this.showingOrderItemDetails = true
    },
    hideOrderItem() {
      this.selectedOrderItemId = null
      this.showingOrderItemDetails = false
    },
    openFireEventModal() {
      this.currentModal = 'fireEvent'
    },
    closeAllModals() {
      this.currentModal = null
    }
  }
}
</script>
